import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Award, CheckCircle2, Star, GraduationCap, Stethoscope, MapPin,
  MessageCircle, Phone, ShieldCheck, TrendingUp, HeartPulse, Baby, Eye,
} from "lucide-react";
import { CLINIC, waLink, telLink, DOCTOR } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { SITE, REF, breadcrumb, graph, CLINIC_NODE } from "../lib/schema";
import SectionHeader from "../components/site/SectionHeader";
import SafeImage from "../components/site/SafeImage";
import GoogleReviewsLive from "../components/site/GoogleReviewsLive";

// This page deliberately targets the 7 highest-intent local queries
// in one place:
//   "best eye doctor in varanasi"       "best eye specialist in varanasi"
//   "best cataract surgeon in varanasi" "best retina specialist in varanasi"
//   "best eye hospital in varanasi"     "best eye checkup in varanasi"
//   "best squint surgeon in varanasi"   "best pediatric ophthalmologist in varanasi"
//
// The URL /best-eye-doctor-varanasi is intentionally exact-match for the
// primary query.  Content stitches together our existing landing pages
// so nothing new gets duplicated — this is a HUB, not a doorway.

const CRITERIA = [
  { icon: GraduationCap, title: "Training pedigree", body: "Where the doctor completed their MS Ophthalmology and any subsequent fellowship. Look for MAMC / Guru Nanak Eye Centre (New Delhi), AIIMS, LVPEI, or Sankara Nethralaya." },
  { icon: TrendingUp, title: "Case volume", body: "A high-volume cataract surgeon (5,000+ lifetime) is generally safer than a low-volume one — surgery is a hand skill. Ask for the count in writing." },
  { icon: Stethoscope, title: "Range of services under one roof", body: "The best eye hospitals handle refraction, cataract, retina, glaucoma, squint, cornea and paediatrics in-house so referrals don't fragment your care." },
  { icon: ShieldCheck, title: "Insurance & Ayushman Bharat", body: "Cashless network coverage matters — most premium IOLs are covered under private policies; Ayushman Bharat covers only an IOL implant worth approx ₹3,000 for eligible beneficiaries, with details clarified at the hospital." },
  { icon: Star, title: "Independent reviews", body: "Focus on Google reviews (harder to fake than website testimonials). Look for consistent 4.7+ across 100+ reviews, including recent ones." },
  { icon: Eye, title: "Modern technology", body: "OCT + OCT-A, MICS phacoemulsification (2.2 mm incision), toric / EDOF / trifocal IOL availability, and pan-retinal laser are the current standard-of-care baseline." },
];

const SERVICES = [
  { icon: Eye, title: "Best cataract surgeon in Varanasi", body: "MICS phacoemulsification with monofocal, toric, EDOF and trifocal IOL options. 10,000+ cataract surgeries by Dr. Shantanu Kumar Gupta. Ayushman Bharat + cashless insurance accepted.", href: "/premium-cataract-surgery-in-varanasi", doctor: "Dr. Shantanu Kumar Gupta" },
  { icon: HeartPulse, title: "Best retina specialist in Varanasi", body: "OCT-guided diabetic retinopathy management, anti-VEGF injections, PRP laser, AMD and RVO care. Handled by Dr. Shantanu Kumar Gupta.", href: "/medical-retina-specialist-in-varanasi", doctor: "Dr. Shantanu Kumar Gupta" },
  { icon: TrendingUp, title: "Complex & complicated cataract", body: "Mature cataracts, subluxated lens, small pupils, post-uveitic and one-eyed patients. Consultant surgeon: Dr. Manisha Mishra.", href: "/complex-cataract-surgery-in-varanasi", doctor: "Dr. Manisha Mishra" },
  { icon: Eye, title: "Squint (Strabismus) surgery in Varanasi", body: "Adults and children. Dainik Jagran (Purvanchal, Jul 2026) featured our squint programme. Consultant surgeon: Dr. Manisha Mishra.", href: "/squint-surgery-in-varanasi", doctor: "Dr. Manisha Mishra" },
  { icon: Baby, title: "Best paediatric ophthalmologist in Varanasi", body: "Amblyopia therapy, paediatric refraction, ROP screening for NICU-graduate infants. Consultant: Dr. Manisha Mishra.", href: "/pediatric-ophthalmologist-in-varanasi", doctor: "Dr. Manisha Mishra" },
  { icon: ShieldCheck, title: "Best comprehensive eye checkup in Varanasi", body: "Full 1-hour examination — refraction, IOP, slit-lamp, dilated fundus and OCT where indicated. Preferred first visit for anyone over 40 or diabetic.", href: "/comprehensive-eye-examination-in-varanasi", doctor: "Dr. Shantanu Kumar Gupta" },
];

const STATS = [
  { k: "10,000+", v: "Cataract surgeries" },
  { k: "150+", v: "Corneal transplants" },
  { k: "12+ yrs", v: "Clinical experience" },
  { k: "4.8★", v: "Google (900+ reviews)" },
];

const FAQS = [
  { q: "Who is the best eye doctor in Varanasi?", a: "Choosing an ophthalmologist is a lifetime decision. At Shantanu Netralaya, Dr. Shantanu Kumar Gupta (MBBS, MS from MAMC / Guru Nanak Eye Centre, New Delhi; DNB; FICO Merit) handles cataract, cornea and medical retina — with a personal count of 10,000+ cataract surgeries. Dr. Manisha Mishra (MBBS Gold Medalist; MS MAMC; DNB; FICO Merit) handles complex cataract, squint and paediatric ophthalmology. Both are fellowship-trained at New Delhi tertiary centres." },
  { q: "Which is the best eye hospital in Varanasi?", a: "The 'best' eye hospital depends on the condition you need treated. For high-volume cataract surgery with premium IOL choices, a boutique tertiary clinic (like Shantanu Netralaya) offers more personalised care than a chain hospital. For emergency vitreoretinal surgery or complex glaucoma, larger tertiary centres may be needed. Ask specifically about the surgeon's personal case volume for YOUR condition." },
  { q: "Which is the best cataract surgeon in Varanasi?", a: "Look for a surgeon with a personal count of 5,000+ cataract surgeries, MICS phacoemulsification training, and the full range of IOL options (monofocal, toric, EDOF, trifocal). Dr. Shantanu Kumar Gupta meets all three criteria — 10,000+ personal surgeries, MICS phaco at 2.2 mm incision, and MAMC / Guru Nanak Eye Centre training." },
  { q: "Which is the best retina specialist in Varanasi?", a: "Retina care is medical (injections + laser) rather than surgical in most cases. The best retina specialist is someone with a fellowship in medical retina, OCT-guided diagnostic protocols, and consistent monthly follow-up capacity. Dr. Shantanu Kumar Gupta trained in medical retina at Guru Nanak Eye Centre, New Delhi and provides OCT + OCT-A guided anti-VEGF and PRP laser treatment." },
  { q: "Where can I get the best squint or paediatric eye treatment in Varanasi?", a: "Squint and paediatric ophthalmology are subspecialties requiring specific training. Dr. Manisha Mishra at Shantanu Netralaya handles both — she's MBBS Gold Medalist, MS from MAMC (New Delhi), and was featured in Dainik Jagran (Purvanchal edition, Jul 2026) for making modern squint surgery accessible to eastern UP patients." },
];

const BestOfVaranasi = () => {
  usePageMeta({
    title: "Best Eye Doctor & Eye Hospital in Varanasi | Shantanu Netralaya",
    path: "/best-eye-doctor-varanasi",
    description:
      "The best eye specialist, cataract surgeon, retina specialist, squint surgeon and paediatric ophthalmologist in Varanasi — Dr. Shantanu Kumar Gupta (MAMC / GNEC New Delhi) & Dr. Manisha Mishra (MBBS Gold Medalist). 10,000+ cataract surgeries. Ayushman Bharat accepted.",
  });

  const jsonLd = useMemo(() => graph([
    breadcrumb([
      { name: "Home", path: "/" },
      { name: "Best Eye Doctor in Varanasi", path: "/best-eye-doctor-varanasi" },
    ]),
    CLINIC_NODE,
    {
      "@type": "FAQPage",
      "@id": `${SITE}/best-eye-doctor-varanasi#faq`,
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ]), []);
  usePageJsonLd(jsonLd);

  return (
    <div data-testid="best-of-varanasi-page">
      {/* HERO */}
      <section className="pt-20 pb-10 container-page">
        <nav aria-label="Breadcrumb" className="mb-6 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          <Link to="/" className="hover:text-[hsl(var(--primary))]">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-[hsl(var(--foreground))]/80">Best Eye Doctor in Varanasi</span>
        </nav>
        <div className="overline mb-4 inline-flex items-center gap-2">
          <Award size={14} strokeWidth={1.5} /> Varanasi's Trusted Eye Care
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-4xl">
          Best Eye Doctor, Cataract Surgeon &amp; Retina Specialist in Varanasi
        </h1>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl">
          Choosing the right eye specialist is a lifetime decision. Here's a candid guide to what actually matters — training, case volume, technology and reviews — plus a summary of every specialty service available at Shantanu Netralaya, Durgakund, Varanasi.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={waLink("Hi, I'd like to book an eye appointment at Shantanu Netralaya, Varanasi.")} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="best-cta-whatsapp">
            <MessageCircle size={16} /> Book on WhatsApp
          </a>
          <a href={telLink()} className="btn-ghost" data-testid="best-cta-call">
            <Phone size={14} /> {CLINIC.phone}
          </a>
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-border pt-8">
          {STATS.map((s) => (
            <div key={s.v}>
              <div className="font-serif text-3xl text-[hsl(var(--primary))]">{s.k}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Live Google reviews — pulled fresh every 6h */}
      <GoogleReviewsLive variant="grid" limit={6} />

      {/* WHAT MAKES A GREAT EYE DOCTOR */}
      <section className="py-14 border-t border-border container-page" data-testid="best-criteria">
        <SectionHeader overline="How to choose" title="What actually makes an eye doctor 'best'" />
        <p className="text-sm text-muted-foreground max-w-2xl mt-3 mb-8">
          Google searches rank clinics by ad spend and SEO, not clinical outcomes. Here are the six criteria that genuinely matter — apply them to any clinic you're considering, including ours.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          {CRITERIA.map((c) => (
            <div key={c.title} className="rounded-2xl border border-border p-5 bg-background flex gap-4">
              <c.icon size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))] shrink-0 mt-0.5" />
              <div>
                <div className="font-serif text-lg">{c.title}</div>
                <p className="text-sm text-muted-foreground mt-1">{c.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES INDEX — the "best X in Varanasi" cluster */}
      <section className="py-14 border-t border-border container-page" data-testid="best-services">
        <SectionHeader overline="Every service under one roof" title="Specialised eye care at Shantanu Netralaya" />
        <p className="text-sm text-muted-foreground max-w-2xl mt-3 mb-8">
          Two consultants working together — Dr. Shantanu Kumar Gupta (cataract, cornea, medical retina) and Dr. Manisha Mishra (complex cataract, squint, paediatric ophthalmology).
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          {SERVICES.map((s) => (
            <Link key={s.href} to={s.href} className="group rounded-2xl border border-border p-5 bg-background hover:border-[hsl(var(--primary))] transition-colors" data-testid={`best-service-${s.href.replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-start gap-4">
                <s.icon size={20} strokeWidth={1.5} className="text-[hsl(var(--accent))] shrink-0 mt-0.5" />
                <div>
                  <div className="font-serif text-lg group-hover:text-[hsl(var(--primary))]">{s.title}</div>
                  <p className="text-sm text-muted-foreground mt-1">{s.body}</p>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                    Consultant · {s.doctor}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DOCTOR BIOS */}
      <section className="py-14 border-t border-border container-page" data-testid="best-doctors">
        <SectionHeader overline="Our clinical team" title="Two doctors, complementary specialties" />
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {[
            { name: "Dr. Shantanu Kumar Gupta", role: "Cataract · Cornea · Medical Retina", credentials: "MBBS · MS (MAMC & Guru Nanak Eye Centre) · DNB · FICO (Merit)", vol: "10,000+ cataract surgeries · 150+ corneal transplants", href: "/about", portrait: DOCTOR.portrait },
            { name: "Dr. Manisha Mishra", role: "Complex Cataract · Squint · Pediatric Ophthalmology", credentials: "MBBS (Gold Medalist, 4 distinctions) · MS (MAMC) · DNB · FICO (Merit)", vol: "Featured in Dainik Jagran, Jul 2026 (10 UP districts)", href: "/team/dr-manisha-mishra", portrait: "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4ykhz87v_F43F41D7-9C9C-464E-90EE-6356CEF829FD.png" },
          ].map((d) => (
            <Link key={d.name} to={d.href} className="group rounded-2xl border border-border overflow-hidden bg-background hover:border-[hsl(var(--primary))] transition-colors flex flex-col sm:flex-row">
              <div className="w-full sm:w-40 h-48 sm:h-auto bg-[hsl(var(--secondary))]/40 shrink-0">
                <SafeImage src={d.portrait} alt={d.name} label={d.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="font-serif text-xl group-hover:text-[hsl(var(--primary))]">{d.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mt-1">{d.role}</div>
                <div className="text-xs text-muted-foreground mt-2">{d.credentials}</div>
                <div className="text-xs mt-2 text-[hsl(var(--foreground))]/70">{d.vol}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 border-t border-border container-page" data-testid="best-faq">
        <SectionHeader overline="Frequently asked" title="Common questions from Varanasi patients" />
        <div className="mt-8 space-y-4">
          {FAQS.map((f, i) => (
            <details key={i} className="group rounded-2xl border border-border p-5 bg-background">
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                <div className="font-serif text-lg">{f.q}</div>
                <span className="text-[hsl(var(--accent))] shrink-0 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* LOCATION FOOTER */}
      <section className="py-14 border-t border-border bg-[hsl(var(--secondary))]/40" data-testid="best-location">
        <div className="container-page grid md:grid-cols-2 gap-10">
          <div>
            <div className="overline mb-3 inline-flex items-center gap-2">
              <MapPin size={13} /> Where we practise
            </div>
            <h2 className="font-serif text-3xl">Shantanu Netralaya Eye Hospital</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              40, Deen Dayal Colony, Kabir Nagar Road, Near Durgakund, Varanasi 221005 · Plus Code 7XRW+32.
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Mon-Sat 10:00-20:00 · Sun 10:00-14:00 by appointment.
            </p>
          </div>
          <div className="rounded-2xl border border-border overflow-hidden bg-background">
            <iframe
              src="https://maps.google.com/maps?q=25.2836,82.9871&z=15&output=embed"
              className="w-full h-64"
              loading="lazy"
              title="Shantanu Netralaya on Google Maps"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default BestOfVaranasi;
